/**
 * Factura, nota de crédito y nota de débito sobre el papel PREIMPRESO de la imprenta (bloque 11, 2026-10-09).
 *
 * El papel ya trae cabecera, logo, «forma libre», número de control y pie de imprenta: el sistema imprime solo el
 * cuerpo —cliente, número, fecha, condición, renglones y totales— en el lugar que marca la plantilla de la sucursal
 * (márgenes en mm, calibrables con la hoja de prueba). Una forma libre solo es válida sobre ese papel: no hay modo
 * «hoja en blanco». Solo lo fiscal y solo bolívares: sin columna de código, sin tasa ni dólares, sin «atendido por»
 * ni «recibí conforme», sin marca de copia (respuestas de la clienta).
 *
 * El IGTF que sale es el percibido AL EMITIR (cobro de la misma operación): Total = base + exento + IVA + IGTF. Un
 * cobro posterior en divisas no cambia el papel de una factura ya emitida (su IGTF va al reporte del IGTF).
 *
 * Letra de ancho fijo y renglones a 6 por pulgada para la Epson LX-300+II; cada producto ocupa UN renglón, así la
 * cantidad que cabe en la hoja es fija (`renglones`) y la caja no emite facturas que no quepan en una hoja.
 */
import { formatearCantidad, HOJA_MM, Money, PAPEL_MM, type DocumentoDTO, type EmpresaImpresionDTO, type PlantillaFactura } from '@mg/shared';
import { formatearFechaHora } from '@/lib/utils.ts';
import type { CSSProperties } from 'react';

const fecha = (v: string) => formatearFechaHora(v, { hour: undefined, minute: undefined });
/**
 * Hora del sistema en que se emitió el documento, en hora de Caracas («01:09 p. m.», como el recibo de abono). Sale de
 * `emitido_en`: una reimpresión dice la hora de emisión, no la de impresión (pedido de la dueña 2026-10-19).
 */
export const horaEmision = (v: string) => formatearFechaHora(v, { day: undefined, month: undefined, year: undefined });
const cant = (v: string) => formatearCantidad(Money.parse(v));
const TITULO: Record<string, string> = { FAC: 'FACTURA', NC: 'NOTA DE CRÉDITO', ND: 'NOTA DE DÉBITO' };
/** Alto de un renglón: 6 líneas por pulgada, el paso normal de la matriz de punto. */
const RENGLON_MM = 4.233;
/**
 * Letra para la impresora de matriz de puntos (pedido de la dueña 2026-10-20: «no se logra entender nada», letra muy
 * pequeña y muy junta). Courier New tiene trazos muy finos y sale borrosa en una matricial; Lucida Console (en todo
 * Windows) es de ancho fijo, con trazo parejo y minúsculas altas. Va en negrita (la matricial la hace más oscura), a 11 pt
 * con un poco más de separación: unos 10,5 caracteres por pulgada, cerca de los 10 cpi de la impresora, en vez de 12.
 * El renglón sigue midiendo 1/6 de pulgada y los márgenes no cambian.
 *
 * Negrita dibujada (2026-10-28): Lucida Console no trae negrita ni cursiva propias. Chrome las simulaba, y desde Chrome 156
 * (se actualizó solo el 27-10) se ven en pantalla pero no salen en el papel. Por eso la negrita es un contorno negro
 * alrededor de cada letra y la cursiva de los rótulos, una inclinación: Chrome los imprime en cualquier versión.
 */
export const LETRA_MATRIZ = "'Lucida Console', 'Lucida Sans Typewriter', Consolas, 'Courier New', monospace";
const MATRIZ_PT = 11;
/**
 * La negrita simulada ensanchaba cada letra de 2,425 a 2,503 mm. Sin ella, la separación sube de 0,25 a 0,47 pt para que
 * cada letra siga ocupando 2,503 mm y nada se mueva en la hoja (medido en Chrome 151 y 156).
 */
const MATRIZ_ESPACIO_PT = 0.47;
/**
 * Grosor del contorno que hace la negrita: 0,5 px (orden del usuario del 2026-11-03, «bájala un poco»; antes 0,7 px).
 * El contorno no cambia el ancho de las letras: nada se mueve en la hoja.
 */
export const MATRIZ_TRAZO_PX = 0.5;
/** Inclinación de los rótulos («Cliente :», «Fecha Emisión»…), parecida a la cursiva simulada de antes. */
export const MATRIZ_INCLINACION = 'skewX(-12deg)';
/**
 * El número de la factura va 1,2 veces más grande y su parte de arriba pasaba unos 0,3 mm por encima del margen superior
 * (donde empieza a imprimir el sistema). Se baja 0,6 mm sin mover nada más: los números no tienen colita y el renglón de
 * abajo empieza 1 mm más abajo (2026-10-28).
 */
const NUMERO_BAJA = '0.6mm';
/** Ancho de un carácter así, medido en Chrome: 2,503 mm, con un poco de margen. */
const MM_POR_CARACTER = 2.51;
/**
 * En la matricial, un monto que no cabe en su columna se achica lo justo en vez de cortarse: un monto fiscal nunca sale
 * incompleto. `columnaMm` es el ancho de la columna (con 1 mm de relleno a cada lado).
 */
function estiloMonto(matriz: boolean, texto: string, columnaMm: number): CSSProperties | undefined {
  return ajustarTexto(matriz, texto, Math.floor((columnaMm - 2.5) / MM_POR_CARACTER), true).estilo;
}

/**
 * Un dato de la cabecera (o un monto) en la matricial: si no cabe en `caben` caracteres, primero se achica (hasta 8 pt;
 * un monto, lo que haga falta) y solo si aun así no cabe, se corta en un carácter completo. Así no queda media letra
 * que se lea como otra.
 */
function ajustarTexto(matriz: boolean, texto: string, caben: number, monto = false): { texto: string; estilo: CSSProperties | undefined } {
  if (!matriz || texto.length <= caben) return { texto, estilo: undefined };
  const pt = (MATRIZ_PT * caben) / texto.length;
  if (monto || pt >= 8) return { texto, estilo: { fontSize: `${pt.toFixed(2)}pt`, letterSpacing: 0 } };
  return { texto: texto.slice(0, Math.floor((caben * MATRIZ_PT) / 8)), estilo: { fontSize: '8pt', letterSpacing: 0 } };
}

/** ¿El texto cabe en `renglones` renglones de `porRenglon` caracteres, saltando de renglón entre palabras como el navegador? */
function cabeEnRenglones(texto: string, porRenglon: number, renglones: number): boolean {
  let renglon = 1;
  let usado = 0;
  for (const palabra of texto.split(/\s+/).filter(Boolean)) {
    const necesita = usado === 0 ? palabra.length : usado + 1 + palabra.length;
    if (necesita <= porRenglon) usado = necesita;
    else {
      renglon += 1;
      usado = palabra.length;
    }
  }
  return renglon <= renglones && usado <= porRenglon;
}

/**
 * El domicilio tiene sus dos renglones fijos (pedido de la dueña 19-10-2026: se cortaba). En la matricial, si con la letra
 * normal no cabe, la letra se achica lo justo (hasta 8 pt) antes de cortarse. Nunca ocupa un renglón más: la lista de
 * productos y los totales quedan donde los calibró la hoja de prueba.
 */
function estiloDomicilio(matriz: boolean, texto: string, columnaMm: number): CSSProperties | undefined {
  if (!matriz) return undefined;
  for (let pt = MATRIZ_PT; pt >= 8; pt -= 0.25) {
    if (cabeEnRenglones(texto, Math.floor(columnaMm / ((MM_POR_CARACTER * pt) / MATRIZ_PT)), 2)) return pt === MATRIZ_PT ? undefined : { fontSize: `${pt.toFixed(2)}pt`, letterSpacing: 0 };
  }
  return { fontSize: '8pt', letterSpacing: 0 };
}


interface Fila {
  id: string;
  descripcion: string;
  unidades: string;
  unidad: string;
  precioBs: Money;
  descuentoPct: Money;
  totalBs: Money;
}

/**
 * Renglones en Bs tal como se guardaron (precio × tasa redondeado por unidad, × cantidad): lo que cuadra a mano.
 * Cada producto ocupa un renglón de `ancho` caracteres: si no cabe con el detalle de la presentación, sale sin él.
 */
function filasDe(d: DocumentoDTO, ancho: number): Fila[] {
  return d.lineas.map((l) => {
    const factor = Money.parse(l.unidades_presentacion);
    // Presentaciones completas: unidades ÷ factor (la cantidad guardada va redondeada a 2 decimales y no sirve para saberlo)
    const presentaciones = Money.parse(l.unidades).div(factor.esCero() ? '1' : factor);
    const detalle = factor.gt('1') && presentaciones.decimal.isInteger() ? ` · ${cant(presentaciones.toString())} × ${l.presentacion_nombre}` : '';
    const descripcion = (l.producto_nombre + detalle).length <= ancho ? l.producto_nombre + detalle : l.producto_nombre.slice(0, ancho);
    return { id: l.id, descripcion, unidades: cant(l.unidades), unidad: l.producto_unidad, precioBs: Money.parse(l.precio_unitario_bs), descuentoPct: Money.parse(l.descuento_pct), totalBs: Money.parse(l.subtotal_bs) };
  });
}

/** Mismo instante (el cobro de emisión se registra en la misma transacción que el documento). */
const mismoInstante = (a: string, b: string) => Math.abs(new Date(a).getTime() - new Date(b).getTime()) < 1000;

function trocear<T>(xs: T[], n: number): T[][] {
  const r: T[][] = [];
  for (let i = 0; i < xs.length; i += n) r.push(xs.slice(i, i + n));
  return r.length > 0 ? r : [[]];
}

const marcas = (hasta: number) => Array.from({ length: Math.floor(hasta / 10) }, (_, i) => (i + 1) * 10);

/** Reglas en milímetros y líneas de los márgenes: solo en la hoja de prueba. */
function Reglas({ p, ancho, alto }: { p: PlantillaFactura; ancho: number; alto: number }) {
  const guia = { position: 'absolute', borderColor: '#000', borderStyle: 'dashed', borderWidth: 0, fontSize: '6.5pt', fontFamily: 'Arial, sans-serif', lineHeight: 1 } as const;
  return (
    <div data-testid="formato-reglas" style={{ position: 'absolute', inset: 0 }}>
      {marcas(ancho).map((x) => (
        <div key={`x${x}`} style={{ position: 'absolute', left: `${x}mm`, top: 0, height: x % 50 === 0 ? '4mm' : '2.5mm', borderLeft: '0.2mm solid #000', fontSize: '6pt', fontFamily: 'Arial, sans-serif', lineHeight: 1, paddingLeft: '0.5mm', paddingTop: '2.6mm' }}>{x}</div>
      ))}
      {marcas(alto).map((y) => (
        <div key={`y${y}`} style={{ position: 'absolute', top: `${y}mm`, left: 0, width: y % 50 === 0 ? '3mm' : '1.5mm', borderTop: '0.2mm solid #000', fontSize: '5pt', fontFamily: 'Arial, sans-serif', lineHeight: 1, paddingLeft: '0.2mm', whiteSpace: 'nowrap' }}>{y}</div>
      ))}
      <div style={{ ...guia, top: `${p.superior_mm}mm`, left: 0, right: 0, borderTopWidth: '0.2mm' }}>
        <span style={{ position: 'absolute', left: '40mm', bottom: '0.8mm', lineHeight: 1 }}>superior {p.superior_mm} mm · aquí empieza a imprimir el sistema</span>
      </div>
      <div style={{ ...guia, top: `${alto - p.inferior_mm}mm`, left: 0, right: 0, borderTopWidth: '0.2mm' }}>
        <span style={{ position: 'absolute', left: '40mm', top: '0.8mm', lineHeight: 1 }}>inferior {p.inferior_mm} mm · aquí terminan los totales</span>
      </div>
      <div style={{ ...guia, left: `${p.izquierdo_mm}mm`, top: 0, bottom: 0, borderLeftWidth: '0.2mm' }} />
      <div style={{ ...guia, left: `${ancho - p.derecho_mm}mm`, top: 0, bottom: 0, borderLeftWidth: '0.2mm' }} />
    </div>
  );
}

export function FormatoFiscal({ d, e, plantilla, reglas = false, reimpresion = false }: { d: DocumentoDTO; e: EmpresaImpresionDTO; plantilla?: PlantillaFactura | undefined; reglas?: boolean; reimpresion?: boolean }) {
  const p = plantilla ?? d.sucursal_plantilla;
  const formato = d.sucursal_formato_impresion;
  const { ancho, alto } = PAPEL_MM[formato];
  // Media carta en hoja carta de pie (2026-11-03): la página es la hoja carta entera y la factura va en la mitad de arriba
  const hoja = HOJA_MM[formato];
  const enMitad = hoja.alto > alto;
  const conDescuento = d.lineas.some((l) => Money.parse(l.descuento_pct).esPositivo());
  const matriz = p.fuente === 'matriz';
  // Caracteres que caben en la columna del producto: con la letra de la matriz de puntos, MM_POR_CARACTER (unos 10,5 cpi);
  // con la de la láser, 2,117 mm por carácter (12 cpi) como siempre
  const anchoProducto = Math.max(20, Math.floor((ancho - p.izquierdo_mm - p.derecho_mm - 30 - 32 - 36 - (conDescuento ? 16 : 0) - 2) / (matriz ? MM_POR_CARACTER : 2.117)));
  const filas = filasDe(d, anchoProducto);
  const paginas = trocear(filas, p.renglones);
  const titulo = TITULO[d.tipo] ?? d.tipo;
  const anchoCuerpo = ancho - p.izquierdo_mm - p.derecho_mm;
  // Primer renglón (pedido de la dueña 19-10-2026: el nombre largo se cortaba): el número del documento va pegado al
  // margen derecho y el nombre del cliente usa todo lo demás del renglón, en vez de quedarse en la columna de la izquierda.
  // La cabecera sigue con sus 4 renglones: ni la lista de productos ni los totales se mueven, y la hora sigue debajo de
  // «Cond. de Pago» (pedido de la dueña 18-10-2026). Ancho del número en la matricial: título, 3 mm, número a 1,2 em y
  // «· hoja n/n» si sale en varias hojas
  const sufijoHoja = paginas.length > 1 ? ` · hoja ${paginas.length}/${paginas.length}` : '';
  const anchoNumeroMm = (titulo.length + sufijoHoja.length) * MM_POR_CARACTER + 3 + d.numero_formateado.length * MM_POR_CARACTER * 1.2;
  const caracteresNombre = Math.floor((anchoCuerpo - anchoNumeroMm - 4) / MM_POR_CARACTER) - 'Cliente : '.length;
  const nombreCliente = ajustarTexto(matriz, d.cliente_razon_social, caracteresNombre);
  // Columna del cliente debajo del nombre (todo menos los 74 mm de la fecha, la condición y la hora, y 4 mm de
  // separación): en la matricial su teléfono se recorta en un carácter completo, para que no quede media letra
  const columnaClienteMm = anchoCuerpo - 78;
  const caracteresCliente = Math.floor(columnaClienteMm / MM_POR_CARACTER);
  // En la matricial el rótulo es «Telf. :» con 3 mm de aire (y no «Teléfonos :» con 8 mm): con la letra más grande, el
  // teléfono no cabría en el renglón del RIF
  const rotuloTelefono = matriz ? 'Telf. :' : 'Teléfonos :';
  const telefonoCliente = ajustarTexto(matriz, d.cliente_telefono ?? '', caracteresCliente - 2 - 'Rif / Cédula : '.length - d.cliente_rif.length - ` ${rotuloTelefono} `.length);
  const domicilio = d.cliente_direccion_fiscal || '';
  const domicilioEstilo = estiloDomicilio(matriz, `Domicilio : ${domicilio}`, columnaClienteMm);
  // Bs fiscales guardados en el documento (base + exento + IVA = total; IVA = % de la base en Bs): la regla del libro de ventas
  const subtotalBs = Money.sumar(filas.map((f) => f.totalBs));
  const baseBs = Money.parse(d.base_bs);
  const exentoBs = Money.parse(d.exento_bs);
  const ivaBs = Money.parse(d.iva_bs);
  const totalBs = Money.parse(d.total_bs);
  // Descuento global en Bs: lo que separa la suma de los renglones (ya con su descuento) de la base
  const descuentoBs = Money.parse(d.descuento_pct).esPositivo() ? subtotalBs.sub(baseBs).sub(exentoBs).redondear2() : Money.cero();
  // IGTF percibido AL EMITIR la factura o la nota de débito, en Bs a la tasa BCV del cobro. Los cobros posteriores no
  // cambian el papel de un documento ya emitido. La nota de crédito no lleva IGTF.
  const conIgtf = d.pagos.filter((x) => Money.parse(x.igtf_usd).esPositivo() && mismoInstante(x.recibido_en, d.emitido_en));
  const igtfBs = Money.sumar(conIgtf.map((x) => Money.parse(x.igtf_bs)));
  const igtfPct = Money.parse(conIgtf[0]?.igtf_pct ?? e.igtf_pct);
  const llevaIgtf = d.tipo !== 'NC' && (igtfBs.esPositivo() || igtfPct.esPositivo());
  const origenIgtfBs = Money.parse(d.documento_origen_igtf_bs ?? '0');
  const condicion = d.condicion === 'credito' ? `CRÉDITO${d.vence_en ? ` (vence ${fecha(d.vence_en)})` : ''}` : 'CONTADO';
  // Renglones de la columna derecha (74 mm, unos 29 caracteres): si no caben, la letra se achica en vez de cortarse
  // (a crédito, «Cond. de Pago CRÉDITO (vence …)» perdía la fecha de vencimiento en la matricial)
  const estiloDerecha = (texto: string) => ajustarTexto(matriz, texto, Math.floor(74 / MM_POR_CARACTER), true).estilo;
  const horaTexto = horaEmision(d.emitido_en);
  return (
    <div className="fiscal" data-testid="formato-documento" data-formato={formato} data-hojas={paginas.length} data-reimpresion={reimpresion ? 'si' : 'no'}>
      <style>{`
        @page { size: ${hoja.ancho}mm ${hoja.alto}mm; margin: 0; }
        html, body { background: #fff; margin: 0; }
        .fiscal { color: #000; font-family: ${matriz ? LETRA_MATRIZ : 'Arial, Helvetica, sans-serif'}; font-size: ${matriz ? MATRIZ_PT : 9.5}pt; ${matriz ? `letter-spacing: ${MATRIZ_ESPACIO_PT}pt; font-weight: normal;` : 'font-weight: normal;'} line-height: ${RENGLON_MM}mm; }
        .fiscal .hoja { position: relative; width: ${hoja.ancho}mm; height: ${hoja.alto - 0.6}mm; overflow: hidden; box-sizing: border-box; break-after: page; page-break-after: always; }
        .fiscal .hoja:last-of-type { break-after: auto; page-break-after: auto; }
        .fiscal .cuerpo { position: absolute; top: ${p.superior_mm}mm; left: ${p.izquierdo_mm}mm; right: ${p.derecho_mm}mm; }
        .fiscal .pie { position: absolute; bottom: ${hoja.alto - alto + p.inferior_mm}mm; left: ${p.izquierdo_mm}mm; right: ${p.derecho_mm}mm; display: flex; justify-content: space-between; align-items: flex-end; gap: 4mm; }
        .fiscal table { width: 100%; border-collapse: collapse; table-layout: fixed; }
        .fiscal th, .fiscal td { padding: 0 1mm; text-align: left; font-weight: inherit; white-space: nowrap; overflow: hidden; vertical-align: top; }
        .fiscal .num { text-align: right; }
        /* Un renglón mide SIEMPRE 1/6 de pulgada: las letras de otro tamaño dentro de la línea no la estiran */
        .fiscal .hoja span, .fiscal .hoja b { line-height: 0; }
        .fiscal .enc th { border-top: 0.3mm solid #000; border-bottom: 0.3mm solid #000; }
                .fiscal .cli { display: flex; justify-content: space-between; gap: 4mm; }
        .fiscal .cli .dato { white-space: nowrap; overflow: hidden; }
        .fiscal .cli .dom { height: ${RENGLON_MM * 2}mm; overflow: hidden; white-space: normal; }
        ${matriz ? `.fiscal .cuerpo, .fiscal .pie { -webkit-text-stroke: ${MATRIZ_TRAZO_PX}px #000; }
        .fiscal .hoja b { font-weight: inherit; }
        .fiscal .rot { display: inline-block; transform: ${MATRIZ_INCLINACION}; }` : '.fiscal .rot { font-style: italic; }'}
        .fiscal .igtf { border: 0.3mm solid #000; border-radius: 2mm; padding: 0.5mm 2mm; font-size: 0.78em; line-height: 3.4mm; max-width: 74mm; text-transform: uppercase; }
        .fiscal .aviso { font-family: system-ui, sans-serif; font-size: 12px; line-height: 1.4; background: #fef3c7; color: #000; padding: 8px 12px; max-width: ${ancho}mm; margin: 12px auto 0; box-sizing: border-box; }
        .fiscal .aviso.alerta { background: #fee2e2; }
        @media screen { body { background: #e5e7eb; } .fiscal .hoja { background: #fff; box-shadow: 0 0 8px rgba(0,0,0,.2); margin: 12px auto; } }
        @media print { .fiscal .solo-pantalla { display: none !important; } }
      `}</style>
      {/* Ayuda para quien imprime: no sale en el papel */}
      {reimpresion ? (
        <div className="aviso alerta solo-pantalla" data-testid="formato-aviso-reimpresion">
          <b>REIMPRESIÓN.</b> Esta {titulo.toLowerCase()} ya se abrió para imprimir{d.control_formateado ? <>; le corresponde la hoja con el N° de control <b data-testid="formato-control">{d.control_formateado}</b></> : null}. Si esa hoja no llegó a salir (cancelaste o la impresora no imprimió), imprímela ahora en ella. Si ya salió, <b>No uses otra hoja preimpresa</b>: gastaría un número de control que el sistema no conoce. Imprime en hoja en blanco o sobre la copia de esa misma hoja. Si la hoja original se dañó, usa «Hoja dañada» en Documentos: anula ese número y reemite con el siguiente. Esta reimpresión queda en auditoría.
        </div>
      ) : (
        <div className="aviso solo-pantalla" data-testid="formato-aviso-papel">
          <b>Papel preimpreso de {d.sucursal_nombre}</b> ({formato === 'media_carta' ? 'media carta horizontal, 8,5 × 5,5 in' : enMitad ? 'media carta en la mitad de arriba de una hoja carta de pie; después se corta' : 'carta'}).
          {d.control_formateado ? <> Coloca la hoja con el N° de control <b data-testid="formato-control">{d.control_formateado}</b>.</> : null} {enMitad ? 'En el cuadro de impresión: papel «Carta», márgenes «Predeterminados», escala 100 %, sin encabezados ni pies.' : 'En el cuadro de impresión: márgenes «Ninguno», escala 100 %, sin encabezados ni pies.'}
        </div>
      )}
      {paginas.length > 1 ? (
        <div className="aviso alerta solo-pantalla" data-testid="formato-aviso-hojas">
          Esta {titulo.toLowerCase()} tiene {filas.length} renglones y en la hoja caben {p.renglones}: sale en {paginas.length} hojas. Solo la primera va en la hoja preimpresa con su número de control; imprime las demás en hoja en blanco y engrápalas. La caja ya no emite facturas que no quepan en una hoja.
        </div>
      ) : null}
      {paginas.map((lote, i) => (
        <section className="hoja" key={i} data-testid="formato-hoja">
          {reglas ? <Reglas p={p} ancho={ancho} alto={alto} /> : null}
          {reglas && enMitad ? (
            <div data-testid="formato-linea-corte" style={{ position: 'absolute', top: `${alto}mm`, left: 0, right: 0, borderTop: '0.3mm dashed #000', fontSize: '6.5pt', fontFamily: 'Arial, sans-serif', lineHeight: 1 }}>
              <span style={{ position: 'absolute', left: '40mm', top: '0.8mm' }}>mitad de la hoja ({alto} mm) · aquí se corta</span>
            </div>
          ) : null}
          <div className="cuerpo">
            {/* Renglón 1: el nombre usa todo el ancho hasta el número, que va pegado al margen derecho */}
            <div className="cli" data-testid="formato-renglon-cliente">
              <div className="dato" style={{ flex: 1, minWidth: 0 }}><span className="rot">Cliente :</span> <span data-testid="formato-cliente" style={nombreCliente.estilo}>{nombreCliente.texto}</span></div>
              {/* El número va a 1,2 em: sin recortar arriba (pedido de la dueña del 22-10). El nombre sí se recorta al ancho */}
              <div className="dato" style={{ flexShrink: 0, overflow: 'visible' }} data-testid="formato-titulo">{titulo} <b style={{ fontSize: '1.2em', marginLeft: '3mm', position: 'relative', top: NUMERO_BAJA }} data-testid="formato-numero">{d.numero_formateado}</b>{paginas.length > 1 ? ` · hoja ${i + 1}/${paginas.length}` : ''}</div>
            </div>
            {/* Renglones 2 a 4: domicilio en dos y RIF a la izquierda; fecha, condición y, debajo de la condición, la hora
                (pedido de la dueña 2026-10-19). Siguen siendo 4 renglones en total: los productos no se mueven */}
            <div className="cli">
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="dom" style={domicilioEstilo} data-testid="formato-domicilio"><span className="rot">Domicilio :</span> {domicilio}</div>
                <div className="dato">
                  <span className="rot">Rif / Cédula :</span> {d.cliente_rif}
                  <span className="rot" style={{ marginLeft: matriz ? '3mm' : '8mm' }}>{rotuloTelefono}</span> <span style={telefonoCliente.estilo}>{telefonoCliente.texto}</span>
                </div>
              </div>
              <div style={{ width: '74mm' }}>
                <div className="dato" style={estiloDerecha(`Fecha Emisión ${fecha(d.emitido_en)}`)}><span className="rot">Fecha Emisión</span> <b data-testid="formato-fecha">{fecha(d.emitido_en)}</b></div>
                <div className="dato" style={estiloDerecha(`Cond. de Pago ${condicion}`)} data-testid="formato-condicion"><span className="rot">Cond. de Pago</span> {condicion}</div>
                <div className="dato" style={estiloDerecha(`Hora Emisión ${horaTexto}`)}><span className="rot">Hora Emisión</span> <span data-testid="formato-hora">{horaTexto}</span></div>
              </div>
            </div>
            {/* Nota de crédito o de débito: número, fecha y monto de la factura que afecta (Providencia SNAT/2011/00071, art. 22) */}
            {d.tipo !== 'FAC' && d.documento_origen_numero && d.documento_origen_total_bs ? (
              <div style={{ whiteSpace: 'nowrap', overflow: 'hidden' }} data-testid="formato-afectada">
                Factura afectada N° {d.documento_origen_numero}
                {d.documento_origen_emitido_en ? ` de fecha ${fecha(d.documento_origen_emitido_en)}` : ''} por Bs {Money.parse(d.documento_origen_total_bs).toDisplay()}
                {origenIgtfBs.esPositivo() ? ` más IGTF Bs ${origenIgtfBs.toDisplay()}` : ''}
              </div>
            ) : null}
            <table data-testid="formato-renglones">
              <colgroup>
                <col />
                <col style={{ width: '30mm' }} />
                <col style={{ width: '32mm' }} />
                {conDescuento ? <col style={{ width: '16mm' }} /> : null}
                <col style={{ width: '36mm' }} />
              </colgroup>
              <thead>
                <tr className="enc">
                  <th>PRODUCTO</th>
                  <th className="num">CANT.</th>
                  <th className="num">PRECIO UNIT.</th>
                  {conDescuento ? <th className="num">%DSCTO</th> : null}
                  <th className="num">TOTAL</th>
                </tr>
              </thead>
              <tbody>
                {lote.map((f) => (
                  <tr key={f.id} data-testid="formato-renglon">
                    <td>{f.descripcion}</td>
                    <td className="num" data-testid="formato-linea-cantidad">
                      {f.unidades} <span style={{ fontSize: '0.75em' }}>{f.unidad}</span>
                    </td>
                    <td className="num" style={estiloMonto(matriz, f.precioBs.toDisplay(), 32)}>{f.precioBs.toDisplay()}</td>
                    {conDescuento ? <td className="num">{f.descuentoPct.esPositivo() ? f.descuentoPct.toDisplay() : ''}</td> : null}
                    <td className="num" style={estiloMonto(matriz, f.totalBs.toDisplay(), 36)}>{f.totalBs.toDisplay()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {i < paginas.length - 1 ? <div style={{ textAlign: 'right' }} data-testid="formato-continua">CONTINÚA EN LA HOJA SIGUIENTE</div> : null}
          </div>
          {i === paginas.length - 1 ? (
            <div className="pie" data-testid="formato-pie">
              <div>{llevaIgtf && e.leyendas.factura_igtf ? <div className="igtf" data-testid="formato-leyenda-igtf">{e.leyendas.factura_igtf.replace(/(\d) %/g, '$1\u00a0%')}</div> : null}</div>
              <table style={{ width: '104mm' }}>
                <colgroup>
                  <col />
                  <col style={{ width: '32mm' }} />
                  <col style={{ width: '36mm' }} />
                </colgroup>
                <tbody>
                  <tr>
                    <td className="num" colSpan={2}>Sub Total</td>
                    <td className="num" data-testid="formato-subtotal-bs" style={estiloMonto(matriz, subtotalBs.toDisplay(), 36)}>{subtotalBs.toDisplay()}</td>
                  </tr>
                  {descuentoBs.esPositivo() ? (
                    <tr>
                      <td className="num" colSpan={2}>Descuento {Money.parse(d.descuento_pct).toString()}%</td>
                      <td className="num" data-testid="formato-descuento-bs" style={estiloMonto(matriz, `−${descuentoBs.toDisplay()}`, 36)}>−{descuentoBs.toDisplay()}</td>
                    </tr>
                  ) : null}
                  <tr>
                    <td className="num" colSpan={2}>Monto Exento</td>
                    <td className="num" data-testid="formato-exento-bs" style={estiloMonto(matriz, exentoBs.toDisplay(), 36)}>{exentoBs.toDisplay()}</td>
                  </tr>
                  <tr>
                    <td className="num">I.V.A {Money.parse(d.iva_pct).toString()}%</td>
                    <td className="num" data-testid="formato-base-bs" style={estiloMonto(matriz, baseBs.toDisplay(), 32)}>{baseBs.toDisplay()}</td>
                    <td className="num" data-testid="formato-iva-bs" style={estiloMonto(matriz, ivaBs.toDisplay(), 36)}>{ivaBs.toDisplay()}</td>
                  </tr>
                  {llevaIgtf ? (
                    <tr>
                      <td className="num" colSpan={2}>IGTF {igtfPct.toString()}%</td>
                      <td className="num" data-testid="formato-igtf" style={estiloMonto(matriz, igtfBs.toDisplay(), 36)}>{igtfBs.toDisplay()}</td>
                    </tr>
                  ) : null}
                  <tr>
                    <td className="num" colSpan={2}><b>Total Bs.:</b></td>
                    <td className="num" style={estiloMonto(matriz, totalBs.add(igtfBs).toDisplay(), 36)}><b data-testid="formato-total-bs">{totalBs.add(igtfBs).toDisplay()}</b></td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : null}
        </section>
      ))}
    </div>
  );
}
